import {
  Box,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useEffect, useRef } from "react";

const TextInput = styled(TextField)(({ theme }) => ({
  "& .MuiInputLabel-root": {
    color: theme.palette.blackDisabled.main,
    backgroundColor: theme.palette.white.main,
    paddingRight: 4,
    fontSize: "18px",
    lineHeight: "20px",
    "&.Mui-focused": {
      color: theme.palette.blackDisabled.main,
      backgroundColor: theme.palette.white.main,
      paddingRight: 4,
      fontSize: "18px",
    },
  },

  "& .MuiOutlinedInput-root": {
    color: theme.palette.blackDisabled.main,
    "& input": {
      color: theme.palette.black.main,
      padding: "10px 16px 8px 16px",
      height: "30px",
      fontSize: "20px",
      width: "100%",
      zIndex: 10,
    },
    "& fieldset": {
      border: "2px solid",
      borderColor: theme.palette.blackDisabled.main,
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.black.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.black.main,
      boxShadow: `0px 2px 0px ${theme.palette.black.main}`,
    },
    "&.MuiOutlinedInput-root": {
      padding: 0,
      paddingRight: "8px",
    },
    "& textarea": {
      color: theme.palette.black.main,
      padding: "10px 16px 8px 16px",
      height: "30px",
      fontSize: "18px",
      width: "100%",
      maxHeight: "80px",
      lineHeight: "30px",
    },
  },
}));

const CustomInput = ({
  loading,
  value,
  onChange,
  label,
  req,
  startIcon,
  endIcon,
  endIconClick,
  endIconDisabled,
  multiline,
  minLength,
  maxLength,
  helperText,
  autoComplete,
  disabled,
  focused,
  bgColor,
}) => {
  const theme = useTheme();
  const inputRef = useRef();

  const handleError = () => {
    let errorText;
    if (value.length < minLength) {
      errorText = `The ${label} can be a minimum of ${minLength} letters`;
    }
    if (value.length > maxLength) {
      errorText = `The ${label} can be a maximum of ${maxLength} letters`;
    }

    return errorText;
  };
  const handleBorderColor = () => {
    let bColor;
    if (disabled) {
      bColor = theme.palette.blackDisabled.main;
    } else {
      if (handleError()) {
        bColor = theme.palette.danger.main;
      }
    }
    return bColor;
  };

  useEffect(() => {
    focused && inputRef.current.focus();
  }, [inputRef, focused]);

  return (
    <Box>
      <Box>
        {!loading ? (
          <TextInput
            inputRef={inputRef}
            autoComplete={autoComplete ? "off" : ""}
            sx={{
              width: "100%",
              "& input": {
                pl: startIcon && "0px !IMPORTANT",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "2px solid",
                  borderColor: handleError() && theme.palette.danger.main,
                  borderRadius: "8px",
                  backgroundColor: bgColor ? bgColor : "",
                },
                "&:hover fieldset": {
                  borderColor: handleBorderColor(),
                },
                "&.Mui-focused fieldset": {
                  borderColor: handleError() && theme.palette.danger.main,
                  boxShadow:
                    handleError() && `0px 2px 0px ${theme.palette.danger.main}`,
                },
              },
            }}
            name='username'
            value={value}
            onChange={onChange}
            multiline={multiline}
            disabled={disabled}
            placeholder={value === "" ? label : ""}
            InputProps={{
              startAdornment: startIcon && (
                <InputAdornment sx={{ pl: "8px" }} position='start'>
                  {startIcon}
                </InputAdornment>
              ),
              endAdornment: endIcon && (
                <InputAdornment position='end'>
                  <button
                    disabled={endIconDisabled}
                    style={{
                      cursor: endIconDisabled ? "" : "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    type={"submit"}
                    onClick={endIconClick}
                  >
                    {endIcon}
                  </button>
                </InputAdornment>
              ),
            }}
            {...(label &&
              value !== "" && {
                label: (
                  <Box sx={{ display: "flex" }}>
                    {label}
                    <Box
                      sx={{
                        color: "danger.main",
                        pl: "3px",
                        lineHeight: "17px",
                      }}
                    >
                      {req && "*"}
                    </Box>
                  </Box>
                ),
              })}
          />
        ) : (
          <Skeleton
            width={"100%"}
            height={multiline ? 90 : 48}
            sx={{ transform: "none" }}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography
          sx={{
            px: "16px",
            fontSize: "11px",
            mt: "6px",
            color: handleError() ? "danger.main" : "textField.main",
          }}
        >
          {handleError() ? handleError() : helperText}
        </Typography>
        {maxLength && (
          <Typography
            sx={{
              px: "16px",
              fontSize: "11px",
              mt: "6px",
              color: handleError() ? "danger.main" : "textField.main",
            }}
          >
            {value.length + "/" + maxLength}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CustomInput;
